import React, { useState } from 'react';
import Container from 'react-bootstrap/Container';
import { useHistory } from 'react-router-dom';
import { FaRegAddressBook } from 'react-icons/fa';
import FormControl from 'react-bootstrap/FormControl';
import Row from 'react-bootstrap/Row';
import FormGroup from 'react-bootstrap/FormGroup';
import Loader from 'react-loader';
import FormLabel from 'react-bootstrap/FormLabel';
import Col from 'react-bootstrap/Col';
import { Controller, useForm } from 'react-hook-form';
import { TextMaskCustom } from '../Patient/PatientProfile';
import { chrApi } from '../../api/api';
import { toast } from 'react-toastify';
import { get } from 'lodash';
import { useMutation } from 'react-query';
import { useQMEByApptId } from './hooks';
import { CloseButton, SaveButton } from '../../components/ActionButtons';
import useToggle from '../../hooks/useToggle';
import EmployerLookupPane from './EmployerLookupPane';

function EmployerInformationForm(props) {
  const [employerPane, toggleEmployerPane] = useToggle(false);
  const [employerId, setEmployerId] = useState(undefined);
  const history = useHistory();
  const appointmentId = get(props, 'match.params.appointmentId');
  const { control, reset, handleSubmit, errors } = useForm({
    mode: 'onSubmit',
    defaultValues: {
      employerDetails: {
        name: '',
        jobTitle: '',
        address: {
          street: '',
          city: '',
          state: '',
          zip: '',
        },
        phone: '',
        fax: '',
      },
    },
  });

  const sendBackToQME = () => {
    history.push(`/qme_appointment/${appointmentId}`);
  };

  const { data, isLoading } = useQMEByApptId(appointmentId, (res) => {
    const { employerDetails } = res;
    setEmployerId(employerDetails?.id);
    reset({ employerDetails });
  });

  const qmeId = data?.id;

  const { mutate } = useMutation(
    (formData) => {
      return chrApi('put', `medical_evaluation/${qmeId}`, formData);
    },
    {
      mutationKey: 'updateQmeEmployerInfo',
      onSuccess: () => {
        toast.success('Employer Information Successfully Updated');
        sendBackToQME();
      },
    },
  );

  function onSubmit(formData) {
    let data = { ...formData };
    if (employerId) {
      data.employerDetails.id = employerId;
    }

    mutate(data);
  }

  if (isLoading) {
    return <Loader loaded={false} />;
  }

  return (
    <Container fluid>
      <p className={'h4 color-777 mb-3'}>QME-AME Employer Info</p>
      <Container fluid className={'bg-white px-4 py-3'}>
        <div className={'align-items-center d-flex flex-row mb-3'}>
          <p className={'h5 mb-0'}>Employer Information</p>
          <FaRegAddressBook fontSize={18} className={'ml-2 csr-pointer'} onClick={toggleEmployerPane} />
        </div>
        <Row>
          <Col xs={2}>
            <FormGroup controlId={'employerDetails.name'}>
              <FormLabel>{'Employer Name*'}</FormLabel>
              <Controller
                name={'employerDetails.name'}
                control={control}
                defaultValue={''}
                rules={{ required: true }}
                render={({ onChange, value }, { invalid }) => <FormControl isInvalid={invalid} onChange={(event) => onChange(event.target.value)} value={value} type={'text'} />}
              />
              <FormControl.Feedback type={'invalid'}>This field is required</FormControl.Feedback>
            </FormGroup>
          </Col>
          <Col xs={2}>
            <FormGroup controlId={'employerDetails.jobTitle'}>
              <FormLabel>{'Job Title'}</FormLabel>
              <Controller
                name={'employerDetails.jobTitle'}
                control={control}
                defaultValue={''}
                render={({ onChange, value }) => <FormControl onChange={(event) => onChange(event.target.value)} value={value} type={'text'} />}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col xs={2}>
            <FormGroup controlId={'employerDetails.address.street'}>
              <FormLabel>{'Street'}</FormLabel>
              <Controller
                name={'employerDetails.address.street'}
                control={control}
                defaultValue={''}
                render={({ onChange, value }) => <FormControl onChange={(event) => onChange(event.target.value)} value={value} type={'text'} />}
              />
            </FormGroup>
          </Col>
          <Col xs={2}>
            <FormGroup controlId={'employerDetails.address.city'}>
              <FormLabel>{'City'}</FormLabel>
              <Controller
                name={'employerDetails.address.city'}
                control={control}
                defaultValue={''}
                render={({ onChange, value }) => <FormControl onChange={(event) => onChange(event.target.value)} value={value} type={'text'} />}
              />
            </FormGroup>
          </Col>
          <Col xs={2}>
            <FormGroup controlId={'employerDetails.address.state'}>
              <FormLabel>{'State'}</FormLabel>
              <Controller
                name={'employerDetails.address.state'}
                control={control}
                defaultValue={''}
                render={({ onChange, value }) => <FormControl onChange={(event) => onChange(event.target.value)} value={value} type={'text'} />}
              />
            </FormGroup>
          </Col>
          <Col xs={2}>
            <FormGroup controlId={'employerDetails.address.zip'}>
              <FormLabel>{'Zip'}</FormLabel>
              <Controller
                name={'employerDetails.address.zip'}
                control={control}
                defaultValue={''}
                render={({ onChange, value }) => <FormControl onChange={(event) => onChange(event.target.value)} value={value} type={'number'} />}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col xs={2}>
            <FormGroup controlId={'employerDetails.phone'}>
              <FormLabel>{'Phone'}</FormLabel>
              <Controller
                name={'employerDetails.phone'}
                control={control}
                defaultValue={''}
                rules={{
                  validate: (value) => !value || value.length === 10,
                }}
                as={<TextMaskCustom isInvalid={errors?.employerDetails?.phone} />}
              />
            </FormGroup>
          </Col>
          <Col xs={2}>
            <FormGroup controlId={'employerDetails.fax'}>
              <FormLabel>{'Fax'}</FormLabel>
              <Controller
                name={'employerDetails.fax'}
                control={control}
                defaultValue={''}
                rules={{
                  validate: (value) => !value || value.length === 10,
                }}
                as={<TextMaskCustom isInvalid={errors?.employerDetails?.fax} />}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row className={'mt-3'}>
          <Col>
            <SaveButton className={'mr-1'} onClick={handleSubmit(onSubmit)} />
            <CloseButton onClick={sendBackToQME} />
          </Col>
        </Row>
      </Container>
      <EmployerLookupPane
        isOpen={employerPane}
        onClose={toggleEmployerPane}
        onSelect={(employer) => {
          const { id: empId } = employer;
          setEmployerId(empId);
          reset({ employerDetails: employer });
        }}
      />
    </Container>
  );
}

EmployerInformationForm.propTypes = {};
EmployerInformationForm.defaultProps = {};

export default EmployerInformationForm;
